/**
 * Centrality Data Loader
 *
 * Loads pre-computed centrality metrics from centrality.json
 * Generated by: scripts/generate-centrality.js
 */

export interface SkillCentrality {
  degree: number;
  normalizedDegree: number;
}

export interface OccupationCentrality {
  avgSkillDegree: number;
  normalizedCentrality: number;
}

export interface CentralityMetadata {
  generatedAt: string;
  taxonomyVersion: string;
  skillCount: number;
  occupationCount: number;
  maxSkillDegree: number;
  maxOccupationAvgDegree: number;
  relationCount: number;
}

export interface CentralityData {
  skills: Record<string, SkillCentrality>;
  occupations: Record<string, OccupationCentrality>;
  metadata: CentralityMetadata;
}

// Cache for loaded data
let centralityCache: CentralityData | null = null;
let loadPromise: Promise<CentralityData> | null = null;

/**
 * Load centrality data from JSON file
 * Returns cached data if already loaded
 */
export async function loadCentralityData(): Promise<CentralityData> {
  // Return cached data if available
  if (centralityCache) {
    return centralityCache;
  }

  // Return existing promise if load is in progress
  if (loadPromise) {
    return loadPromise;
  }

  // Start loading
  loadPromise = (async () => {
    try {
      const basePath = import.meta.env.BASE_URL || '/';
      const url = new URL('data/centrality.json', window.location.origin + basePath).href;

      console.log('[centralityLoader] Loading from:', url);
      const response = await fetch(url);

      if (!response.ok) {
        throw new Error(`Failed to load centrality data: ${response.status}`);
      }

      const data: CentralityData = await response.json();
      centralityCache = data;

      console.log('[centralityLoader] Loaded centrality for', data.metadata.skillCount, 'skills and', data.metadata.occupationCount, 'occupations');

      return data;
    } catch (error) {
      console.warn('[centralityLoader] Failed to load centrality data, using defaults:', error);
      // Return empty default data
      centralityCache = getDefaultCentralityData();
      return centralityCache;
    }
  })();

  return loadPromise;
}

/**
 * Get centrality for a skill (0-1 normalized)
 */
export function getSkillCentrality(
  data: CentralityData | null,
  skillId: string
): number {
  if (!data || !data.skills[skillId]) {
    return 0.5; // Default to middle
  }
  return data.skills[skillId].normalizedDegree;
}

/**
 * Get raw degree for a skill
 */
export function getSkillDegree(
  data: CentralityData | null,
  skillId: string
): number {
  if (!data || !data.skills[skillId]) {
    return 0;
  }
  return data.skills[skillId].degree;
}

/**
 * Get centrality for an occupation (0-1 normalized)
 */
export function getOccupationCentrality(
  data: CentralityData | null,
  occupationId: string
): number {
  if (!data || !data.occupations[occupationId]) {
    return 0.5; // Default to middle
  }
  return data.occupations[occupationId].normalizedCentrality;
}

/**
 * Get average skill degree for an occupation
 */
export function getOccupationAvgSkillDegree(
  data: CentralityData | null,
  occupationId: string
): number {
  if (!data || !data.occupations[occupationId]) {
    return 0;
  }
  return data.occupations[occupationId].avgSkillDegree;
}

/**
 * Get default/empty centrality data
 */
function getDefaultCentralityData(): CentralityData {
  return {
    skills: {},
    occupations: {},
    metadata: {
      generatedAt: '',
      taxonomyVersion: '',
      skillCount: 0,
      occupationCount: 0,
      maxSkillDegree: 0,
      maxOccupationAvgDegree: 0,
      relationCount: 0
    }
  };
}

/**
 * Check if centrality data is loaded
 */
export function isCentralityLoaded(): boolean {
  return centralityCache !== null;
}

/**
 * Get cached centrality data (may be null if not loaded)
 */
export function getCachedCentralityData(): CentralityData | null {
  return centralityCache;
}
